import { createSlice, createAsyncThunk, PayloadAction, miniSerializeError, SerializedError } from '@reduxjs/toolkit';
import { resetStore } from 'src/store';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';

import { getCountryCodes } from 'src/api/isp/adminContact';

import { MembershipDetailDto, ServerError } from 'src/types';

type MembershipState = {
  isView: boolean | null;
  selectedMembership: MembershipDetailDto | null;
  countryCodes: {
    isLoading: boolean;
    countryCodeList: string[];
    serverErrorMessages: string[];
  };
};

const serializeError = (e: unknown): SerializedError => {
  const message = new ServerErrorAdapter(e as ServerError).combine().join('\n  ');
  return miniSerializeError({ message });
};

export const fetchCountryCodeList = createAsyncThunk(
  'adminContact/fetchCountryCodeList',
  async (companyId: string) => {
    const res = await getCountryCodes(companyId);
    return res.data;
  },
  { serializeError },
);

const initialState: MembershipState = {
  isView: null,
  selectedMembership: null,
  countryCodes: {
    isLoading: false,
    countryCodeList: [],
    serverErrorMessages: [],
  },
};

const membershipSlice = createSlice({
  name: 'membership',
  initialState,
  reducers: {
    setMembership(state: MembershipState, action: PayloadAction<MembershipDetailDto>) {
      state.selectedMembership = action.payload;
    },
    setIsViewMembership(state: MembershipState, action: PayloadAction<boolean | null>) {
      state.isView = action.payload;
    },
    resetMembership(state: MembershipState) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCountryCodeList.pending, (state: MembershipState) => {
      state.countryCodes.isLoading = true;
    });
    builder.addCase(fetchCountryCodeList.fulfilled, (state: MembershipState, action) => {
      state.countryCodes.isLoading = false;
      state.countryCodes.countryCodeList = action.payload;
    });
    builder.addCase(fetchCountryCodeList.rejected, (state: MembershipState, action) => {
      state.countryCodes.isLoading = false;
      state.countryCodes.serverErrorMessages = action.error.message ? [action.error.message] : [];
    });

    // Cleanup
    builder.addCase(resetStore, (state: MembershipState) => {
      Object.assign(state, initialState);
    });
  },
});

export const { setMembership, setIsViewMembership, resetMembership } = membershipSlice.actions;

export default membershipSlice.reducer;
